import { useContext, useEffect, memo, ComponentProps, ReactNode } from 'react';
import { ZoomContext } from 'Guide/Schematics/Diagram/Providers/d3Zoomable';
import { KeypressContext } from '../Providers/keypressContext';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';

const ZOOMSTEP = 0.25;

type ToolButtonProps = ComponentProps<'button'> & {
   icon?: ReactNode;
};

function ToolButton({ className, icon, ...props }: ToolButtonProps) {
   return (
      <button
         className={cn(
            'bg-white border border-gray-300 h-10 w-10 flex items-center justify-center',
            'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300',
            className
         )}
         {...props}
      >
         {icon}
      </button>
   );
}

type ZoomButtonGroupProps = ComponentProps<'div'>;

function ZoomButtonGroup({ className, ...props }: ZoomButtonGroupProps) {
   return (
      <div
         className={cn(
            'shadow-2xl rounded-md fixed z-[9999] flex flex-col overflow-hidden',
            className
         )}
         {...props}
      />
   );
}

const registerShortCuts = (zoomIn: () => void, zoomOut: () => void) => {
   // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook "useContext" is called in "registerShortCuts" that is neither a React function component nor a custom Hook ...
   const keypressContext = useContext(KeypressContext);
   // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook "useContext" is called in "registerShortCuts" that is neither a React function component nor a custom Hook ...
   const zoomContext = useContext(ZoomContext);
   // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook "useEffect" is called in "registerShortCuts" that is neither a React function component nor a custom Hook ...
   useEffect(() => {
      const mappings = [
         { id: 'Zoom', key: '+', action: zoomIn, overlayText: '+' },
         { id: 'Zoom', key: '=', action: zoomIn, overlayText: '+' },
         { id: 'Zoom', key: '-', action: zoomOut, overlayText: '-' },
         { id: 'Zoom', key: '_', action: zoomOut, overlayText: '-' },
      ];

      keypressContext.pushMappings(mappings);

      return () => {
         keypressContext.popMappings(mappings);
      };
   }, [zoomContext]);
};

const Tools = memo(function ToolsInternal() {
   const zoomContext = useContext(ZoomContext);
   const zoomIn = () => zoomContext.zoomBy(1 + ZOOMSTEP);
   const zoomOut = () => zoomContext.zoomBy(1 - ZOOMSTEP);

   registerShortCuts(zoomIn, zoomOut);

   return (
      // Hidden on mobile, shown on md and above
      <ZoomButtonGroup className="hidden md:flex mt-[110px] ml-8">
         <ToolButton
            onClick={zoomIn}
            icon={<FaIcon name="plus" style="regular" className="text-base" />}
            aria-label="Zoom In Diagram Viewer"
         />
         <ToolButton
            onClick={zoomOut}
            icon={<FaIcon name="minus" style="regular" className="text-base" />}
            aria-label="Zoom Out Diagram Viewer"
         />
      </ZoomButtonGroup>
   );
});

export default Tools;
